import { createAsyncThunk } from "@reduxjs/toolkit";

import { type Ingredient } from "@fitness-app/data-models/entities/Ingredient";
import { type Database } from "@fitness-app/supabase";

import { type AsyncThunkCreator } from "../../../index";
import { NUTRITION_REDUCER_NAME } from "../types";

type Payload = {
  ingredient: Ingredient;
  /**
   * Apki klientki: gdy produkt z tym kodem już jest w bazie, oddaj go zamiast dopisywać
   * duplikat. Panel admina tego nie włącza — tam dietetyk świadomie dodaje wpis.
   */
  reuseExistingBarCode?: boolean;
};

type IngredientInsert = Database["public"]["Tables"]["ingredient"]["Insert"];

/**
 * Z `reuseExistingBarCode` zwraca produkt, który już jest w bazie pod tym samym kodem kreskowym.
 * Krok 1 sprawdza duplikat w indeksie wyszukiwarki, ale między krokami (a w trybie „Ze zdjęć"
 * przez całą analizę) ktoś inny mógł dodać ten sam produkt, a indeks dogania bazę z opóźnieniem.
 */
export const addIngredient = createAsyncThunk<Ingredient | null, Payload, AsyncThunkCreator<string>>(
  `${NUTRITION_REDUCER_NAME}/addIngredient`,
  async (payload, { rejectWithValue, extra: { db, analytics } }) => {
    const { barCode } = payload.ingredient;

    if (payload.reuseExistingBarCode && barCode) {
      const { data: existing, error: lookupError } = await db
        .from("ingredient")
        .select("*")
        .eq("barCode", barCode)
        .limit(1)
        .returns<Ingredient[]>();

      if (lookupError) {
        return rejectWithValue(lookupError.message);
      }

      if (existing[0]) {
        analytics.track("add_ingredient_duplicate_skipped", { ingredientId: existing[0].id });
        return existing[0];
      }
    }

    const { error, data } = await db
      .from("ingredient")
      .insert(payload.ingredient as unknown as IngredientInsert)
      .select()
      .returns<Ingredient[]>();

    if (error) {
      return rejectWithValue(error.message);
    }

    analytics.track("add_ingredient", { source: payload.ingredient.source });

    return data[0] || null;
  },
);
